import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Stack, Text, Group, Box, Button, Skeleton, Card } from '@repletoryreach/sdk-ui';
import { Alert, Tabs, BreadcrumbTrail, PageHeader } from '@repletoryreach/sdk-ui/data-display';
import { useTemplate } from '../../../data/useTemplates';
import { TemplateStatusBadge } from '../components/TemplateStatusBadge';
import { OverviewTab } from './components/OverviewTab';
import { VariablesTab } from './components/VariablesTab';
import { TemplateBody } from './components/TemplateBody';
import { MetaTrail } from './components/MetaTrail';
import { LifecycleTab } from './components/LifecycleTab';
import { formatDateTime, formatRelative } from '../../../lib/format';
import { useT } from '../../../i18n/I18nProvider';
import '../templates.css';

const TABS = ['overview', 'variables', 'components', 'meta', 'lifecycle'];

export default function TemplateViewPage() {
  const t = useT();
  const { id } = useParams();
  const nav = useNavigate();
  const { data, isLoading, error, refetch } = useTemplate(id);
  const tpl = data?.data;

  // The tab lives in the URL so a Meta-review escalation can be linked
  // straight into a ticket, and so browser back walks the tabs rather than
  // leaving the page.
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const tab = requested && TABS.includes(requested) ? requested : 'overview';

  const breadcrumbs = (
    <BreadcrumbTrail
      items={[{ label: t('templates.title'), href: '/templates' }, { label: tpl?.code ?? t('common.loading') }]}
      renderLink={(href, label) => <Link to={href}>{label}</Link>}
    />
  );

  if (isLoading) {
    return (
      <Stack gap="lg">
        {breadcrumbs}
        <Skeleton height={96} />
        <Skeleton height={240} />
      </Stack>
    );
  }

  // A load failure and a missing record are different facts, and reporting a
  // 500 as "not found" is the worst available answer on an audit surface.
  if (error) {
    return (
      <Stack gap="lg">
        {breadcrumbs}
        <Alert color="error" title={t('templates.view.loadError')}>
          <Group gap="sm">
            <Text role="bodySmall">{error instanceof Error ? error.message : t('common.unknownError')}</Text>
            <Button size="xs" variant="outline" onClick={() => void refetch()}>{t('action.retry')}</Button>
          </Group>
        </Alert>
      </Stack>
    );
  }

  if (!tpl) {
    return (
      <Stack gap="lg">
        {breadcrumbs}
        <Alert color="warning" title={t('templates.view.notFound')}>
          <Stack gap="sm">
            <Text role="bodySmall">{t('templates.view.notFoundHint')}</Text>
            <Group gap="sm">
              <Button size="xs" variant="outline" onClick={() => nav('/templates')}>
                {t('templates.view.backToList')}
              </Button>
            </Group>
          </Stack>
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <PageHeader
        breadcrumbs={breadcrumbs}
        eyebrow={t('templates.view.eyebrow')}
        title={tpl.code}
        description={t('templates.description')}
        actions={
          <Group gap="sm" align="center">
            <TemplateStatusBadge status={tpl.status} devFallback={tpl.dev_fallback} metaStatus={tpl.meta_status} />
            <Button size="xs" variant="subtle" onClick={() => nav('/templates')}>
              {t('templates.view.backToList')}
            </Button>
          </Group>
        }
      />

      {/* The facts a person checks before reading a tab: what kind of template
          this is, in which language, and how recently it moved. */}
      <Card withBorder>
        <div className="rr-fact-row">
          <Fact label={t('templates.columns.category')} value={tpl.category} />
          <Fact label={t('common.language')} value={tpl.language} />
          <Fact label={t('common.created')} value={formatDateTime(tpl.created_at)} hint={formatRelative(tpl.created_at)} />
          <Fact label={t('common.updated')} value={formatDateTime(tpl.updated_at)} hint={formatRelative(tpl.updated_at)} />
          <Fact
            label={t('templates.columns.metaStatus')}
            value={tpl.meta_status ?? t('templates.metaStatusNone')}
            hint={tpl.imported_at ? t('templates.view.lastImported', { when: formatRelative(tpl.imported_at) }) : undefined}
          />
        </div>
      </Card>

      <Tabs value={tab} onChange={(next) => setParams({ tab: next }, { replace: true })}>
        <Tabs.List>
          <Tabs.Tab value="overview">{t('common.overview')}</Tabs.Tab>
          <Tabs.Tab value="variables">{t('templates.view.tabs.variables')}</Tabs.Tab>
          <Tabs.Tab value="components">{t('templates.view.tabs.components')}</Tabs.Tab>
          <Tabs.Tab value="meta">{t('templates.view.tabs.meta')}</Tabs.Tab>
          <Tabs.Tab value="lifecycle">{t('templates.view.tabs.lifecycle')}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="overview">
          <Box pt="lg"><OverviewTab template={tpl} /></Box>
        </Tabs.Panel>
        <Tabs.Panel value="variables">
          <Box pt="lg"><VariablesTab template={tpl} /></Box>
        </Tabs.Panel>
        <Tabs.Panel value="components">
          <Box pt="lg"><TemplateBody components={tpl.components} /></Box>
        </Tabs.Panel>
        <Tabs.Panel value="meta">
          <Box pt="lg"><MetaTrail template={tpl} /></Box>
        </Tabs.Panel>
        <Tabs.Panel value="lifecycle">
          <Box pt="lg"><LifecycleTab template={tpl} /></Box>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function Fact({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rr-fact-row__item">
      <Text role="caption" c="var(--rr-color-text-muted)">{label}</Text>
      <Text role="bodySmall" fw={600}>{value}</Text>
      {hint && <Text role="caption" c="var(--rr-color-text-muted)">{hint}</Text>}
    </div>
  );
}
